/** @jsx jsx */
import {
	type MutableRefObject,
	memo,
	type ReactNode,
	useState,
	forwardRef,
	type Ref,
	useCallback,
	type KeyboardEventHandler,
	createContext,
	useContext,
} from 'react';
import { css, keyframes, jsx } from '@compiled/react';
import noop from 'lodash/noop';
import { token } from '@atlaskit/tokens';
import { Popup, type PopupComponentProps } from '@atlaskit/popup';
import type { EntryPointActions } from '@atlassian/jira-entry-point/src/controllers/utils/types.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCellHoverCallbacks, useCellHoverHandlers } from '../controllers/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../constants.tsx';
import { PopupContent, type AnalyticsAttributes } from './popup-content/index.tsx';

export type Props<T extends HTMLElement = HTMLElement> = {
	content?: ReactNode;
	children: (props: { ref: Ref<T>; ariaExpanded?: boolean }) => ReactNode;
	contentRef?: MutableRefObject<T | null>;
	entryPointActions?: EntryPointActions;
	isInteractive?: boolean;
	shouldGetParentRef?: boolean;
	isFixedWidth?: boolean;
	onHoverOut?: () => void;
	hidePopover?: boolean;
	analyticsAttributes?: AnalyticsAttributes;
};

const PopupComponentInternal = forwardRef<HTMLDivElement, PopupComponentProps>(
	(
		{
			children: popupChildren,
			isReferenceHidden,
			shouldRenderToParent,
			shouldFitContainer,
			...restProps
		},
		ref,
	) => {
		const { isFixedWidth, isInteractive, isPopupOpen, setIsPopupOpen } =
			useContext(HoverPopoverContext);

		if (isReferenceHidden && isPopupOpen) {
			setIsPopupOpen(false);
		}

		return (
			// eslint-disable-next-line jsx-a11y/no-static-element-interactions
			<div
				css={[
					containerStyles,
					isInteractive && interactiveStyles,
					isFixedWidth && fixedWidthStyles,
				]}
				onMouseEnter={() => isInteractive && setIsPopupOpen(true)}
				onMouseLeave={() => isInteractive && setIsPopupOpen(false)}
				{...restProps}
				ref={ref}
			>
				{popupChildren}
			</div>
		);
	},
);

const HoverPopoverContext = createContext<{
	isFixedWidth?: boolean;
	isInteractive?: boolean;
	isPopupOpen: boolean;
	setIsPopupOpen: (isPopupOpen: boolean) => void;
}>({
	isFixedWidth: false,
	isInteractive: false,
	isPopupOpen: false,
	setIsPopupOpen: noop,
});

const HoverPopoverComponent = <T extends HTMLElement>({
	content,
	children,
	contentRef,
	entryPointActions,
	isInteractive,
	shouldGetParentRef,
	isFixedWidth,
	onHoverOut,
	hidePopover,
	analyticsAttributes,
}: Props<T>) => {
	const [isPopupOpen, setIsPopupOpen] = useState(false);

	const togglePopup = useCallback(() => {
		setIsPopupOpen(!isPopupOpen);
	}, [isPopupOpen]);

	useCellHoverCallbacks({
		contentRef,
		onPreload: entryPointActions?.preload,
		onLoad: entryPointActions?.load,
		onPopupToggle: setIsPopupOpen,
		shouldGetParentRef,
		onHoverOut,
		onChangePopupState: togglePopup,
	});

	if (content) {
		return fg('nin-grid-keyboard-navigation') ? (
			<UFOSegment name="jira-list-hover-popover" mode="list">
				<JSErrorBoundary
					id="jira-list-hover-popover"
					packageName={PACKAGE_NAME}
					teamName={TEAM_NAME}
					fallback="flag"
				>
					<HoverPopoverContext.Provider
						value={{ isFixedWidth, isInteractive, isPopupOpen, setIsPopupOpen }}
					>
						<Popup
							content={({ update }) => (
								<PopupContent
									isInteractive={isInteractive}
									update={update}
									analyticsAttributes={analyticsAttributes}
								>
									{content}
								</PopupContent>
							)}
							popupComponent={PopupComponentInternal}
							// eslint-disable-next-line jsx-a11y/no-autofocus
							autoFocus={false}
							isOpen={!hidePopover && isPopupOpen}
							onClose={() => setIsPopupOpen(false)}
							trigger={({ ref }) => children({ ref, ariaExpanded: isPopupOpen })}
							placement="bottom-start"
						/>
					</HoverPopoverContext.Provider>
				</JSErrorBoundary>
			</UFOSegment>
		) : (
			<UFOSegment name="jira-list-hover-popover" mode="list">
				<JSErrorBoundary
					id="jira-list-hover-popover"
					packageName={PACKAGE_NAME}
					teamName={TEAM_NAME}
					fallback="flag"
				>
					<Popup
						content={({ update }) => (
							<PopupContent
								isInteractive={isInteractive}
								update={update}
								analyticsAttributes={analyticsAttributes}
							>
								{content}
							</PopupContent>
						)}
						popupComponent={forwardRef<HTMLDivElement, PopupComponentProps>(
							(
								{
									children: popupChildren,
									isReferenceHidden,
									shouldRenderToParent,
									shouldFitContainer,
									...restProps
								},
								ref,
							) => {
								if (isReferenceHidden && isPopupOpen) {
									setIsPopupOpen(false);
								}

								return (
									// eslint-disable-next-line jsx-a11y/no-static-element-interactions
									<div
										css={[
											containerStyles,
											isInteractive && interactiveStyles,
											isFixedWidth && fixedWidthStyles,
										]}
										onMouseEnter={() => isInteractive && setIsPopupOpen(true)}
										onMouseLeave={() => isInteractive && setIsPopupOpen(false)}
										{...restProps}
										ref={ref}
									>
										{popupChildren}
									</div>
								);
							},
						)}
						// eslint-disable-next-line jsx-a11y/no-autofocus
						autoFocus={false}
						isOpen={!hidePopover && isPopupOpen}
						onClose={() => setIsPopupOpen(false)}
						trigger={({ ref }) => children({ ref, ariaExpanded: isPopupOpen })}
						placement="bottom-start"
					/>
				</JSErrorBoundary>
			</UFOSegment>
		);
	}

	return typeof children === 'function' ? children({ ref: null }) : children;
};

// @see https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087#issuecomment-656596623
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const HoverPopover = memo(HoverPopoverComponent) as typeof HoverPopoverComponent;

export type CellHoverRendererProps = {
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	onKeyDown?: KeyboardEventHandler<HTMLElement>;
};

export const CellHoverRenderer = ({
	children,
}: {
	children: (props: CellHoverRendererProps) => ReactNode;
}) => {
	const { handleMouseEnter, handleMouseLeave, handleToggle } = useCellHoverHandlers();
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.code === 'Space') {
				handleToggle();
			}
		},
		[handleToggle],
	);

	return children({
		onMouseEnter: handleMouseEnter,
		onMouseLeave: handleMouseLeave,
		onKeyDown: handleKeyDown,
	});
};

const fadeIn = keyframes({
	from: {
		opacity: '0',
	},
	to: {
		opacity: '100',
	},
});

const interactiveStyles = css({
	marginBlock: token('space.negative.100'),
	paddingBlock: token('space.150'),
	pointerEvents: 'auto',
});

const containerStyles = css({
	marginBlock: token('space.100'),
	animationName: `${fadeIn}`,
	opacity: '0',
	animationDuration: '0.1s',
	animationTimingFunction: 'ease-in',
	animationFillMode: 'forwards',
	maxWidth: '300px',
	pointerEvents: 'none',
});

const fixedWidthStyles = css({
	width: '300px',
});
